/**
 * screens/FollowupsScreen.tsx
 * Follow-ups — the guaranteed floor. Chip counts + per-row assign/dismiss/resolve.
 * UI recreated verbatim from the design HTML (lines ~343-385), max-width 920.
 *
 * Wired to sk-orchestrator: GET/PATCH /v1/voice-console/followups (voiceService,
 * which normalizes the row shape and falls back to mock when no backend/auth).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ActivityIndicator, Pressable } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole } from '../context/voiceConsoleContext';
import { Card, Chip, Button, ScreenBody } from '../components/voiceUi';
import { Followup } from '../data/voiceMock';
import { listFollowups, updateFollowup } from '../services/voice/voiceService';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

export default function FollowupsScreen() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { role, orgId, openCall } = useConsole();

  const [rows, setRows] = useState<Followup[]>([]);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, live: isLive } = await listFollowups();
    setRows(data);
    setLive(isLive);
    setLoading(false);
  }, []);

  // Plain mount/org-change effect — the console shell has no React Navigation,
  // so useReloadOnFocus (useFocusEffect) is NOT available here.
  useEffect(() => { void load(); }, [load, orgId]);

  const open = rows.filter(f => f.status === 'OPEN').length;
  const res = rows.filter(f => f.status === 'RESOLVED').length;
  const dis = rows.filter(f => f.status === 'DISMISSED').length;

  // Mutate on the backend, then reload so counts + order reflect the source of truth.
  const act = async (id: string, patch: { status?: string; assignee?: string | null }, verb: string) => {
    setBusyId(id);
    try {
      const resp = await updateFollowup(id, patch);
      showSuccess(resp, verb);
      await load();
    } catch (err) {
      handleApiError(err, 'Could not update follow-up');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <ScreenBody maxWidth={920}>
      {/* Chip row */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Chip kind="solid">{open} open</Chip>
        <Chip kind="default">{res} resolved</Chip>
        <Chip kind="outline">{dis} dismissed</Chip>
        <View style={{ flex: 1 }} />
        {!live && !loading && (
          <Text style={{ fontSize: 11.5, color: c.ink4, fontFamily: theme.font.mono }}>Couldn’t load — check connection</Text>
        )}
        <Text style={{ fontSize: 11.5, color: c.ink4, fontFamily: theme.font.sans }}>The guaranteed floor — works even without SK Tickets</Text>
      </View>

      {/* Loading */}
      {loading && (
        <View style={{ padding: 40, alignItems: 'center' }}>
          <ActivityIndicator color={c.ink3} />
        </View>
      )}

      {/* Empty state */}
      {!loading && open === 0 && (
        <View style={{ borderWidth: 1, borderStyle: 'dashed', borderColor: c.lineStrong, borderRadius: 10, padding: 36, alignItems: 'center', backgroundColor: c.paper2, marginBottom: 16 }}>
          <Text style={{ fontWeight: '600', color: c.ink2, marginBottom: 4, fontFamily: theme.font.sans }}>All caught up</Text>
          <Text style={{ fontSize: 12.5, color: c.ink3, textAlign: 'center', fontFamily: theme.font.sans }}>No open follow-ups. New ones appear here the moment the agent can't resolve a call.</Text>
        </View>
      )}

      {/* Rows */}
      {!loading && (
        <View style={{ gap: 10 }}>
          {rows.map(f => {
            const isOpen = f.status === 'OPEN';
            const statusKind = f.status === 'OPEN' ? 'default' : f.status === 'RESOLVED' ? 'solid' : 'outline';
            const assigneeTxt = f.assignee ? `Assigned to ${f.assignee}` : 'Unassigned';
            const busy = busyId === f.id;
            // Meta segments — drop any blank (e.g. `why` is absent for real rows).
            const meta = [f.call, f.why, assigneeTxt, f.age ? `${f.age} ago` : ''].filter(Boolean);
            return (
              <Card key={f.id} style={{ padding: 14 }}>
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 13 }}>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, marginBottom: 4, flexWrap: 'wrap' }}>
                      <Text style={{ fontWeight: '600', fontSize: 13.5, color: c.ink, fontFamily: theme.font.sans }}>{f.reason}</Text>
                      <Chip kind={statusKind}>{f.status}</Chip>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {meta.map((seg, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <Text style={{ fontSize: 11.5, color: c.ink3, fontFamily: theme.font.sans }}>·</Text>}
                          <Text style={{ fontSize: 11.5, color: c.ink3, fontFamily: i === 0 ? theme.font.mono : theme.font.sans }}>{seg}</Text>
                        </React.Fragment>
                      ))}
                    </View>
                    {/* Click-through to the originating call (SKVOICE-OUTBOUND §3). */}
                    {f.call && (
                      <Pressable onPress={() => openCall(f.call)} style={{ marginTop: 6 }}>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: c.ink2, fontFamily: theme.font.sans }}>View call detail ›</Text>
                      </Pressable>
                    )}
                  </View>
                  {isOpen && role !== 'viewer' && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, opacity: busy ? 0.5 : 1 }}>
                      <Button kind="ghost" label="Assign to me" onPress={() => act(f.id, { assignee: 'You' }, 'Assigned')} />
                      <Button kind="default" label="Dismiss" onPress={() => act(f.id, { status: 'DISMISSED' }, 'Dismissed')} />
                      <Button kind="primary" label="Resolve" onPress={() => act(f.id, { status: 'RESOLVED' }, 'Resolved')} />
                    </View>
                  )}
                </View>
              </Card>
            );
          })}
        </View>
      )}
    </ScreenBody>
  );
}
